import React, { useContext, useState, useRef, useEffect } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import api from "../api/api";
import OnboardingTour from "./OnboardingTour";
import AIAssistantWidget from "./AIAssistantWidget";
import CompleteProfileModal from "./CompleteProfileModal";
import "../styles/dashboard.css";
import { isSoundEnabled, setSoundEnabled, playNavigate } from "../utils/sounds";
import { io } from "socket.io-client";

import {
  Home, BookOpen, ClipboardList, User, LogOut,
  Search, BarChart2, ChevronRight, X, Bell, PenSquare, Trophy, Settings,
  Volume2, VolumeX, Mail,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

function EmailVerifyBanner({ email }) {
  const [dismissed, setDismissed] = useState(false);
  const [sent,      setSent]      = useState(false);
  const [loading,   setLoading]   = useState(false);

  if (dismissed) return null;

  const sendLink = async () => {
    setLoading(true);
    try {
      await api.post("auth/resend-verification", { email });
      setSent(true);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      background: "linear-gradient(135deg,#fef3c7,#fde68a)",
      border: "1px solid #f59e0b",
      borderRadius: 14,
      padding: "14px 18px",
      margin: "0 0 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      boxShadow: "0 2px 8px rgba(245,158,11,0.12)",
    }}>
      <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>⚠️</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ color: "#92400e", fontSize: "0.88rem" }}>Verify your email address</strong>
        <div style={{ color: "#b45309", fontSize: "0.8rem", marginTop: 2 }}>
          {sent
            ? `✅ Verification link sent to ${email}. Check your inbox.`
            : `Your account email (${email}) is unverified. Verify it to keep full access.`
          }
        </div>
      </div>
      {!sent && (
        <button
          onClick={sendLink}
          disabled={loading}
          style={{
            padding: "7px 16px", background: "#f59e0b", color: "white",
            border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.8rem",
            cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
          }}
        >
          {loading ? "Sending…" : "Send Verification Email"}
        </button>
      )}
      <button
        onClick={() => setDismissed(true)}
        style={{ background: "none", border: "none", cursor: "pointer", color: "#b45309", fontSize: "1rem", flexShrink: 0, padding: "4px" }}
        title="Dismiss"
      >✕</button>
    </div>
  );
}

function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, notifications, unreadCount, markNotificationsRead, markSingleNotificationRead } = useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const [showTour, setShowTour] = useState(!localStorage.getItem('uhc_tour_done'));
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const logoDropdownRef = useRef();
  // Message admin state
  const [showMsgPanel, setShowMsgPanel] = useState(false);
  const [userMessages, setUserMessages] = useState([]);
  const [msgUnreadCount, setMsgUnreadCount] = useState(0);
  const [selectedMsgId, setSelectedMsgId] = useState(null);

  const [showMsgModal, setShowMsgModal] = useState(false);
  const [msgCategory,  setMsgCategory]  = useState("");
  const [msgSubject,   setMsgSubject]   = useState("");
  const [msgBody,      setMsgBody]      = useState("");
  const [msgSending,   setMsgSending]   = useState(false);

  // ── Login Rank Popup ──
  const [rankPopup, setRankPopup] = useState(null); // {rank,overtook,gainedPoint,streak,points}
  const [msgSent,      setMsgSent]      = useState(false);
  const [msgError,     setMsgError]     = useState("");

  const fetchUserMessages = async () => {
    try {
      const res = await api.get("contact/my-messages");
      const msgs = Array.isArray(res.data) ? res.data : [];
      setUserMessages(msgs);
      const unread = msgs.filter(m => m.userRead === false).length;
      setMsgUnreadCount(unread);
    } catch {}
  };

  // Show rank popup once per session after login, or dynamically when rank climbs
  useEffect(() => {
    const raw = sessionStorage.getItem("pendingRankData");
    if (raw) {
      try {
        const data = JSON.parse(raw);
        sessionStorage.removeItem("pendingRankData");
        // Small delay so the dashboard renders first
        setTimeout(() => setRankPopup(data), 900);
      } catch { sessionStorage.removeItem("pendingRankData"); }
    }

    const handleRankToast = (e) => {
      if (e.detail) {
        setRankPopup(e.detail);
      }
    };
    window.addEventListener("show-rank-toast", handleRankToast);
    return () => window.removeEventListener("show-rank-toast", handleRankToast);
  }, []);

  // Auto-dismiss the rank toast after 5 seconds
  useEffect(() => {
    if (rankPopup) {
      const timer = setTimeout(() => setRankPopup(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [rankPopup]);

  useEffect(() => {
    if (!user?._id) return;
    fetchUserMessages();
    const socketUrl = window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://uhc-backend.onrender.com";
    const socket = io(socketUrl);

    socket.on("USER_NOTIFICATION", () => {
      fetchUserMessages();
    });

    return () => socket.disconnect();
  }, [user?._id]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOn(next);
    // Play a soft confirm sound when turning ON
    if (next) setTimeout(() => playNavigate(), 50);
  };

  const isActive = (path) => location.pathname === path;

  const avatarColor = (name = "") => {
    const cols = ["#4255ff","#8b5cf6","#ec4899","#f59e0b","#10b981","#06b6d4"];
    return cols[(name.charCodeAt(0) || 0) % cols.length];
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const proceedLogout = () => {
    const userId = localStorage.getItem("userId");
    localStorage.removeItem(`activeQuiz_${userId}`);
    logout();
    navigate("/");
  };

  const sendMsgToAdmin = async () => {
    if (!msgCategory) { setMsgError("Please select a category"); return; }
    if (!msgBody.trim()) { setMsgError("Message is required"); return; }
    setMsgSending(true); setMsgError("");
    try {
      await api.post("/contact/suggestions", {
        subject: msgSubject.trim(),
        message: msgBody.trim(),
        category: msgCategory
      });
      setMsgSent(true);
      fetchUserMessages();
      setTimeout(() => {
        setMsgSent(false);
        setShowMsgModal(false);
        setMsgSubject("");
        setMsgBody("");
        setMsgCategory("");
      }, 3000);
    } catch (e) {
      setMsgError(e.response?.data?.message || "Failed to send. Try again.");
    } finally { setMsgSending(false); }
  };

  // Close logo dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (logoDropdownRef.current && !logoDropdownRef.current.contains(e.target)) {
        logoDropdownRef.current.classList.remove("active");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch sitewide announcement
  useEffect(() => {
    api.get('settings/globalAnnouncement')
      .then(res => { if (res.data?.value) setAnnouncement(res.data.value); })
      .catch(() => {});
  }, []);


  const navItems = [
    { icon: <Home         size={18}/>, label: "Home",       path: "/dashboard" },
    { icon: <BookOpen     size={18}/>, label: "Study Hub",  path: "/library"   },
    { icon: <ClipboardList size={18}/>, label: "Quiz",      path: "/quiz"      },
    { icon: <PenSquare    size={18}/>, label: "Submit",     path: "/submit"    },
    { icon: <Trophy       size={18}/>, label: "Ranks",      path: "/leaderboard" },
    { icon: <BarChart2    size={18}/>, label: "Progress",   path: "/profile"   },
  ];

  return (
    <div className="dashboard-wrapper">

      {/* ===== TOP NAVBAR ===== */}
      <div className="dashboard-topbar">
        {/* Home for mobile — replaces old logout position */}
        <button
          className="topbar-hamburger"
          onClick={() => navigate('/dashboard')}
          title="Home"
          style={{ color: 'var(--accent)' }}
        >
          <Home size={20} />
        </button>

        {/* Logo */}
        <div className="topbar-logo" onClick={() => navigate("/dashboard")}>
          <div className="logo-animated-text">
            <span className="logo-letter">U</span>
            <span className="logo-letter">H</span>
            <span className="logo-letter">C</span>
          </div>
        </div>

        {/* Search */}
        <div className="topbar-search">
          <Search size={16} className="topbar-search-icon" style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#939bb4" }} />
          <input
            type="text"
            placeholder="Search topics, quizzes, resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchQuery.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
              }
            }}
          />
        </div>

        <div className="topbar-spacer" />

        {/* Right actions */}
        <div className="topbar-nav-group">
          {/* Sound toggle */}
          <button
            id="uhc-sound-toggle"
            className="topbar-avatar-btn"
            onClick={toggleSound}
            title={soundOn ? "Mute sounds" : "Enable sounds"}
            aria-label={soundOn ? "Mute sounds" : "Enable sounds"}
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--surface)",
              border: "1.5px solid var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: soundOn ? "var(--accent)" : "var(--text-muted)",
              cursor: "pointer", flexShrink: 0,
              transition: "all .2s",
            }}
          >
            {soundOn ? <Volume2 size={17} /> : <VolumeX size={17} />}
          </button>

          {/* Messages */}
          <button
            className="topbar-avatar-btn"
            style={{ position: "relative", width: 36, height: 36, borderRadius: "50%", background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text-muted)" }}
            onClick={() => {
              setShowMsgPanel(prev => !prev);
              setShowNotifications(false);
              fetchUserMessages();
            }}
            title="Messages"
          >
            <Mail size={17}/>
            {msgUnreadCount > 0 && (
              <span style={{ position: "absolute", top: -2, right: -2, background: "#ef4444", color: "white", fontSize: "0.6rem", fontWeight: "bold", padding: "2px 6px", borderRadius: "10px", border: "2px solid var(--bg)" }}>
                {msgUnreadCount}
              </span>
            )}
          </button>

          {/* Notifications */}
          <button
            className="topbar-avatar-btn"
            style={{ position: "relative", width: 36, height: 36, borderRadius: "50%", background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text-muted)" }}
            onClick={() => {
              setShowNotifications(true);
              if (unreadCount > 0) markNotificationsRead();
            }}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span style={{ position: "absolute", top: -2, right: -2, background: "#ef4444", color: "white", fontSize: "0.6rem", fontWeight: "bold", padding: "2px 6px", borderRadius: "10px", border: "2px solid var(--bg)" }}>
                {unreadCount}
              </span>
            )}
          </button>

          {/* Contact Admin via WhatsApp */}
          <a
            href="https://wa.me/233598173019?text=Hello%20Admin%2C%20I%20need%20assistance%20on%20UHC"
            target="_blank"
            rel="noopener noreferrer"
            className="topbar-avatar-btn topbar-whatsapp-btn"
            title="Contact Admin on WhatsApp (+233 598173019)"
            aria-label="Contact Admin on WhatsApp"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "#25D366",
              border: "1.5px solid #22c55e",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              cursor: "pointer",
              flexShrink: 0,
              textDecoration: "none",
              transition: "transform 0.18s ease, box-shadow 0.18s ease",
              boxShadow: "0 2px 8px rgba(37, 211, 102, 0.35)",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-1px) scale(1.05)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 211, 102, 0.5)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(37, 211, 102, 0.35)";
            }}
          >
            <FaWhatsapp size={19} />
          </a>

          {/* Settings icon — replaces letter avatar */}
          <button
            className="topbar-avatar-btn"
            onClick={() => navigate("/profile")}
            title="Settings & Profile"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "var(--surface)",
              border: "1.5px solid var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-muted)", cursor: "pointer", flexShrink: 0,
              transition: "all .2s",
            }}
          >
            <Settings size={18} />
          </button>
        </div>
      </div>

      {/* ===== DASHBOARD BODY ===== */}
      <div className="dashboard-body">

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="sidebar-mobile-overlay" onClick={() => setSidebarOpen(false)} />
        )}

        {/* ===== LEFT SIDEBAR ===== */}
        <aside className={`dashboard-sidebar${sidebarOpen ? " sidebar-open" : ""}`}>
          <span className="sidebar-section-label">Navigate</span>

          {navItems.map((item) => (
            <button
              key={item.path}
              className={`sidebar-nav-item ${isActive(item.path) ? "active" : ""}`}
              onClick={() => navigate(item.path)}
            >
              <span className="nav-item-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}

          <div className="sidebar-divider" />
          <span className="sidebar-section-label">Account</span>

          <button className="sidebar-nav-item" onClick={() => navigate("/profile")}>
            <span className="nav-item-icon"><User size={18} /></span>
            Profile
          </button>

          <button className="sidebar-nav-item" onClick={handleLogout}>
            <span className="nav-item-icon"><LogOut size={18} /></span>
            Log out
          </button>

          {/* ── MY STATS Widget ── */}
          {user && (
            <div style={{
              margin: "8px 0",
              background: "linear-gradient(135deg,rgba(66,85,255,.08),rgba(139,92,246,.06))",
              border: "1px solid rgba(66,85,255,.15)",
              borderRadius: 14, padding: "10px 12px",
              cursor: "pointer", transition: "all .18s",
            }}
              onClick={() => navigate("/leaderboard")}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(66,85,255,.14)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Trophy size={13} color="#f59e0b"/>
                  <span style={{ fontSize: ".72rem", fontWeight: 800, color: "var(--text-heading)", letterSpacing: ".3px" }}>MY STATS</span>
                </div>
                <ChevronRight size={12} style={{ color: "#939bb4" }}/>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1, background: "white", borderRadius: 9, padding: "6px 0", textAlign: "center", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: ".88rem", fontWeight: 900, color: "#4255ff" }}>{user.points || 0}</div>
                  <div style={{ fontSize: ".58rem", color: "var(--text-muted)", fontWeight: 600 }}>PTS</div>
                </div>
                <div style={{ flex: 1, background: "white", borderRadius: 9, padding: "6px 0", textAlign: "center", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: ".88rem", fontWeight: 900, color: "#f59e0b" }}>
                    {(user.streak || 0) > 0 ? `🔥${user.streak}` : "—"}
                  </div>
                  <div style={{ fontSize: ".58rem", color: "var(--text-muted)", fontWeight: 600 }}>STREAK</div>
                </div>
              </div>
            </div>
          )}

          {/* Profile mini card at bottom */}
          {user && (
            <div className="sidebar-profile-mini" onClick={() => navigate("/profile")}>
              <div style={{
                width: 34, height: 34, borderRadius: "50%",
                background: avatarColor(user.name || ""),
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "white", fontWeight: 800, fontSize: ".9rem",
                flexShrink: 0,
              }}>
                {(user.name || "U")[0].toUpperCase()}
              </div>
              <div className="sidebar-profile-mini-info">
                <div className="sidebar-profile-mini-name">{user.name || "User"}</div>
                <div className="sidebar-profile-mini-role">{user.category || "Member"}</div>
              </div>
              <ChevronRight size={14} style={{ color: "#939bb4", flexShrink: 0 }} />
            </div>
          )}
        </aside>

        {/* ===== MAIN CONTENT ===== */}
        <main className="dashboard-content">
          {/* Announcement Banner */}
          {announcement && !announcementDismissed && (
            <div className="uhc-announcement">
              <span>📢&nbsp;&nbsp;{announcement}</span>
              <button className="uhc-announcement__close" onClick={() => setAnnouncementDismissed(true)}>✕</button>
            </div>
          )}

          {/* ── Email Verification Caution Banner ── */}
          {user && !user.isEmailVerified && (
            <EmailVerifyBanner email={user.email} />
          )}

          <Outlet context={{ searchQuery }} />
        </main>
      </div>

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="modal-overlay" onClick={() => setShowLogoutModal(false)} style={{ backdropFilter: "blur(4px)" }}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ 
              maxWidth: 320, padding: '24px', borderRadius: '16px', 
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)', textAlign: 'center',
              animation: 'fadeInUp 0.3s ease'
            }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fff1f2', color: '#e11d48', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <LogOut size={24} />
              </div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 8, color: '#0f172a' }}>Sign Out</h2>
              <p style={{ color: '#64748b', marginBottom: 24, fontSize: '0.9rem', lineHeight: 1.4 }}>Are you sure you want to log out of your account?</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', color: '#475569', fontWeight: 600, cursor: 'pointer' }} onClick={() => setShowLogoutModal(false)}>Cancel</button>
                <button style={{ flex: 1, padding: '10px', borderRadius: '8px', background: '#e11d48', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer' }} onClick={proceedLogout}>Logout</button>
              </div>
            </div>
          </div>
        )}

        {/* ===== NOTIFICATIONS SLIDE-OUT ===== */}
        {showNotifications && (
          <div className="modal-overlay" onClick={() => setShowNotifications(false)} style={{ justifyContent: "flex-end", alignItems: "stretch", padding: 0 }}>
            <div className="notifications-panel" onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 380, background: "var(--surface)", height: "100%", boxShadow: "-10px 0 40px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, color: "var(--text-heading)" }}>Notifications</h2>
                <button onClick={() => setShowNotifications(false)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4 }}>
                  <X size={20} />
                </button>
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
                {notifications.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
                    <Bell size={40} style={{ opacity: 0.2, margin: "0 auto 16px" }} />
                    <p style={{ fontSize: "0.9rem" }}>You're all caught up!</p>
                  </div>
                ) : (
                  notifications.map(n => (
                    <div key={n._id} style={{ padding: "16px", borderRadius: 12, background: n.read ? "transparent" : "var(--accent-pale)", marginBottom: 8, display: "flex", gap: 12, border: "1px solid var(--border)", cursor: "pointer", transition: "all 0.2s" }}
                      onClick={() => {
                        if (!n.read && markSingleNotificationRead) {
                          markSingleNotificationRead(n._id);
                        }
                        if (n.type === "MESSAGE") {
                          setShowNotifications(false);
                          setShowMsgPanel(true);
                          fetchUserMessages();
                        } else if (n.actionLink) {
                          navigate(n.actionLink);
                          setShowNotifications(false);
                        }
                      }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--accent)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Bell size={16} />
                      </div>
                      <div>
                        <div style={{ fontSize: "0.9rem", color: "var(--text-heading)", lineHeight: 1.4 }}>{n.message}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 6 }}>{new Date(n.createdAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
          </div>
        )}

        {/* ===== MESSAGES SLIDE-OUT ===== */}
        {showMsgPanel && (
          <div className="modal-overlay" onClick={() => setShowMsgPanel(false)} style={{ justifyContent: "flex-end", alignItems: "stretch", padding: 0 }}>
            <div className="notifications-panel" onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 380, background: "var(--surface)", height: "100%", boxShadow: "-10px 0 40px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, color: "var(--text-heading)" }}>Messages</h2>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button
                    onClick={() => {
                      setShowMsgModal(true);
                      setMsgCategory("");
                      setMsgSubject("");
                      setMsgBody("");
                    }}
                    style={{ padding: "6px 12px", background: "var(--accent)", color: "white", border: "none", borderRadius: 8, fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
                  >
                    New Message
                  </button>
                  <button onClick={() => setShowMsgPanel(false)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4 }}>
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
                {userMessages.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
                    <Mail size={40} style={{ opacity: 0.2, margin: "0 auto 16px" }} />
                    <p style={{ fontSize: "0.9rem" }}>No messages yet.</p>
                  </div>
                ) : (
                  userMessages.map(m => {
                    const isUnread = m.userRead === false;
                    const isExpanded = selectedMsgId === m._id;
                    const catLabel = m.category ? m.category.charAt(0).toUpperCase() + m.category.slice(1) : "Message";
                    
                    return (
                      <div
                        key={m._id}
                        onClick={async () => {
                          setSelectedMsgId(isExpanded ? null : m._id);
                          if (isUnread) {
                            try {
                              await api.patch(`/contact/my-messages/${m._id}/read`);
                              fetchUserMessages();
                            } catch {}
                          }
                        }}
                        style={{
                          padding: "16px",
                          borderRadius: 12,
                          background: isExpanded ? "var(--surface-hover, rgba(255,255,255,0.02))" : "transparent",
                          border: isUnread ? "1.5px solid var(--accent)" : "1px solid var(--border)",
                          marginBottom: 8,
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <span style={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 99,
                            background: m.category === "password" ? "rgba(239,68,68,0.15)" : m.category === "suggestion" ? "rgba(16,185,129,0.15)" : "rgba(148,163,184,0.15)",
                            color: m.category === "password" ? "#f87171" : m.category === "suggestion" ? "#34d399" : "#94a3b8"
                          }}>
                            {catLabel}
                          </span>
                          <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                            {new Date(m.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div style={{ fontWeight: 700, fontSize: "0.88rem", marginTop: 8, color: "var(--text-heading)" }}>
                          {m.subject || "No Subject"}
                        </div>
                        
                        <div style={{
                          fontSize: "0.82rem",
                          color: "var(--text-muted)",
                          marginTop: 6,
                          lineHeight: 1.4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: isExpanded ? "normal" : "nowrap"
                        }}>
                          {m.message}
                        </div>
                        
                        {isExpanded && (
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed var(--border)" }}>
                            {m.adminReply ? (
                              <div style={{ background: "rgba(66,85,255,0.06)", padding: 12, borderRadius: 10, border: "1px solid rgba(66,85,255,0.15)", marginBottom: 12 }}>
                                <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "var(--accent)", display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                                  <span>👤 Admin Reply:</span>
                                </div>
                                <div style={{ fontSize: "0.82rem", color: "var(--text-heading)", lineHeight: 1.4 }}>
                                  {m.adminReply}
                                </div>
                                <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 6, textAlign: "right" }}>
                                  {m.repliedAt ? new Date(m.repliedAt).toLocaleString() : ""}
                                </div>
                              </div>
                            ) : (
                              <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontStyle: "italic", marginBottom: 12 }}>
                                Waiting for admin reply…
                              </div>
                            )}
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMsgCategory(m.category || "others");
                                setMsgSubject(`Re: ${m.subject || "Message"}`);
                                setMsgBody("");
                                setShowMsgModal(true);
                              }}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                padding: "6px 12px", background: "rgba(255,255,255,0.1)",
                                color: "var(--text-heading)", border: "none", borderRadius: 6,
                                fontSize: "0.75rem", fontWeight: 700, cursor: "pointer"
                              }}
                            >
                              Reply
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

      {/* ===== MOBILE BOTTOM NAV ===== */}
      <nav className="uhc-bottom-nav" aria-label="Main navigation">
        {/* Skip Home (now in topbar), show Study Hub → Ranks */}
        {navItems.slice(1, 5).map((item) => (
          <button
            key={item.path}
            className={`uhc-bottom-nav__item${isActive(item.path) ? " active" : ""}`}
            onClick={() => { navigate(item.path); setSidebarOpen(false); }}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
        {/* Logout as last bottom-nav item */}
        <button
          className="uhc-bottom-nav__item"
          onClick={() => setShowLogoutModal(true)}
          style={{ color: '#ef4444' }}
        >
          <LogOut size={20} />
          <span>Exit</span>
        </button>
        <button
          className="uhc-bottom-nav__item"
          onClick={() => {
            setShowMsgPanel(prev => !prev);
            fetchUserMessages();
          }}
          style={{ position: "relative" }}
        >
          <Mail size={20} />
          {msgUnreadCount > 0 && (
            <span style={{ position: "absolute", top: 2, right: 10, background: "#ef4444", color: "white", fontSize: "0.55rem", fontWeight: "bold", padding: "1px 5px", borderRadius: "8px" }}>
              {msgUnreadCount}
            </span>
          )}
          <span>Messages</span>
        </button>
      </nav>

      {/* ===== MESSAGE ADMIN MODAL ===== */}
      {showMsgModal && (
        <div onClick={e => e.target===e.currentTarget && setShowMsgModal(false)}
          style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(15,23,42,.55)", backdropFilter:"blur(6px)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ width:"100%", maxWidth:520, background:"var(--surface,#fff)", borderRadius:"22px 22px 0 0", padding:"26px 22px 32px", boxShadow:"0 -8px 40px rgba(0,0,0,.18)", animation:"slideUp .26s cubic-bezier(.34,1.56,.64,1)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:11, background:"linear-gradient(135deg,#4255ff,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Mail size={16} color="white"/>
                </div>
                <div>
                  <div style={{ fontWeight:800, fontSize:".95rem", color:"var(--text-heading)" }}>Message Admin</div>
                  <div style={{ fontSize:".7rem", color:"var(--text-muted)" }}>We'll reply to your email</div>
                </div>
              </div>
              <button onClick={() => setShowMsgModal(false)} style={{ background:"var(--bg-input,#f1f3f8)", border:"none", borderRadius:99, width:30, height:30, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--text-muted)" }}><X size={14}/></button>
            </div>
            {msgSent ? (
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ fontSize:"2rem", marginBottom:8 }}>✅</div>
                <div style={{ fontWeight:800, color:"#16a34a" }}>Message Sent!</div>
                <div style={{ fontSize:".8rem", color:"var(--text-muted)", marginTop:4 }}>Admin will reply via email and notification.</div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 10 }}>
                  <select
                    value={msgCategory}
                    onChange={(e) => {
                      setMsgCategory(e.target.value);
                      if (msgError) setMsgError("");
                    }}
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 9,
                      border: "1.5px solid var(--border,#e2e8f0)",
                      background: "var(--bg-input,#f1f3f8)", color: "var(--text-heading)",
                      fontSize: ".86rem", outline: "none", boxSizing: "border-box",
                      fontFamily: "inherit", cursor: "pointer"
                    }}
                  >
                    <option value="" disabled>Select a Category *</option>
                    <option value="password">Password Issue / Reset</option>
                    <option value="suggestion">Suggestion</option>
                    <option value="others">Others</option>
                  </select>
                </div>

                <input
                  type="text"
                  placeholder={msgCategory ? "Subject (optional)" : "Please select a category first..."}
                  value={msgSubject}
                  disabled={!msgCategory}
                  onChange={e => setMsgSubject(e.target.value)}
                  style={{
                    width:"100%", padding:"9px 12px", borderRadius:9,
                    border:"1.5px solid var(--border,#e2e8f0)",
                    background: msgCategory ? "var(--bg-input,#f1f3f8)" : "rgba(255,255,255,0.03)",
                    color:"var(--text-heading)", fontSize:".86rem", outline:"none",
                    marginBottom:10, boxSizing:"border-box", fontFamily:"inherit",
                    cursor: msgCategory ? "text" : "not-allowed"
                  }}
                />

                <textarea
                  placeholder={msgCategory ? "Your message…" : "Please select a category first..."}
                  value={msgBody}
                  rows={4}
                  disabled={!msgCategory}
                  onChange={e => { setMsgBody(e.target.value); if (msgError) setMsgError(""); }}
                  style={{
                    width:"100%", padding:"9px 12px", borderRadius:9,
                    border:`1.5px solid ${msgError?"#ef4444":"var(--border,#e2e8f0)"}`,
                    background: msgCategory ? "var(--bg-input,#f1f3f8)" : "rgba(255,255,255,0.03)",
                    color:"var(--text-heading)", fontSize:".86rem", outline:"none",
                    resize:"vertical", fontFamily:"inherit", lineHeight:1.55,
                    boxSizing:"border-box", cursor: msgCategory ? "text" : "not-allowed"
                  }}
                />

                {msgError && <div style={{ color:"#ef4444", fontSize:".72rem", fontWeight:600, marginTop:4 }}>{msgError}</div>}

                <button
                  onClick={sendMsgToAdmin}
                  disabled={msgSending || !msgCategory || !msgBody.trim()}
                  style={{
                    width:"100%", marginTop:12, padding:"12px", borderRadius:11,
                    border:"none",
                    background: (msgSending || !msgCategory || !msgBody.trim()) ? "#94a3b8" : "linear-gradient(135deg,#4255ff,#8b5cf6)",
                    color:"white", fontWeight:800, fontSize:".9rem",
                    cursor: (msgSending || !msgCategory || !msgBody.trim()) ? "not-allowed" : "pointer"
                  }}
                >
                  {msgSending ? "Sending…" : "Send Message"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp { from { transform:translateY(100%); opacity:0; } to { transform:translateY(0); opacity:1; } }
        @keyframes rankToastIn { from { transform:translateX(-50%) translateY(-20px); opacity:0; } to { transform:translateX(-50%) translateY(0); opacity:1; } }
      `}</style>

      {/* ===== ONBOARDING TOUR ===== */}
      {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}

      {/* ===== RANK TOAST ===== */}
      {rankPopup && (
        <div style={{
          position: "fixed",
          top: 72, left: "50%",
          zIndex: 99999,
          animation: "rankToastIn .4s cubic-bezier(.34,1.56,.64,1)",
          transform: "translateX(-50%)",
          whiteSpace: "nowrap",
        }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: rankPopup.overtook > 0
              ? "linear-gradient(135deg,#f59e0b,#ef4444)"
              : "linear-gradient(135deg,#4255ff,#8b5cf6)",
            color: "white",
            padding: "11px 20px 11px 16px",
            borderRadius: 40,
            fontWeight: 700, fontSize: ".88rem",
            boxShadow: "0 8px 32px rgba(0,0,0,.2), 0 0 0 1.5px rgba(255,255,255,.15)",
            fontFamily: "var(--font)",
          }}>
            <span style={{ fontSize: "1.1rem" }}>
              {rankPopup.overtook > 0 ? "⚡" : "🏆"}
            </span>
            {rankPopup.overtook > 0
              ? `You climbed to #${rankPopup.rank}! Overtook ${rankPopup.overtook} ${rankPopup.overtook === 1 ? "person" : "people"}`
              : `You're ranked #${rankPopup.rank} on the leaderboard`
            }
            {rankPopup.gainedPoint && (
              <span style={{ background: "rgba(255,255,255,.25)", borderRadius: 20, padding: "2px 8px", fontSize: ".75rem" }}>
                +1 pt
              </span>
            )}
            <button
              onClick={() => setRankPopup(null)}
              style={{ background: "none", border: "none", color: "white", cursor: "pointer",
                fontWeight: 900, fontSize: "1rem", lineHeight: 1, padding: 0, opacity: .7 }}
            >×</button>
          </div>
        </div>
      )}
      {/* ===== AI ASSISTANT FLOATING WIDGET ===== */}
      <AIAssistantWidget />

      {/* ===== COMPULSORY PROFILE SETUP MODAL ===== */}
      <CompleteProfileModal />
    </div>
  );
}

export default DashboardLayout;